/** Ejemplos, para clases y props
    <PhoneBox
        // className="show-list show-tooltip error"
    />
*/

import React, { Component } from "react";
import { PhoneBoxContainer } from "./phone-box-style";
import { images } from "../../assets/images";
import TooltipBox from "../tooltip-box/tooltip-box";
import BodyText from "../components-text/body-text";
import NoteText from "../components-text/note-text";
import LabelText from "../components-text/label-text";
import { ERROR_MESSAGES } from "../../constants/errorMessages";
import { hasNumber, isNumeric, isValidNumber } from "../../utils/validation";
import { colors } from "../../assets";
import { getCountriesList } from "../../utils/countries";
import { Country } from "../../services/ConstantsServices.dto";
import { withGeneral } from "../../context/mixin/with-general";

interface PhoneBoxProps {
  className?: string;
  disabled?: boolean;
  icon?: string;
  iconFocus?: string;
  iconDisabled?: string;
  required?: boolean;
  errorCode?: string;
  notScroll?: boolean;

  initialValue?: string;
  onChange: (value: string, error?: boolean) => void;
  labelText: string;

  withTooltip?: boolean;
  tooltipTitle?: string;
  tooltipText?: string;
  countries: Country[];
}

interface PhoneBoxState {
  showTooltip: boolean;
  showPrefixList: boolean;
  prefix: string;
  number: string;
  search: string;
  foundPrefixes: Country[];
  errorPrefix: string;
  errorNumber: string;
  focus: boolean;
  [key: string]: any;
  countries: Country[];
  showAllPrefixList: boolean;
}

class PhoneBox extends Component<PhoneBoxProps, PhoneBoxState> {
  private toggleContainer: any;

  constructor(props: PhoneBoxProps) {
    super(props);
    this.state = {
      showTooltip: false,
      showPrefixList: false,
      prefix: "+34",
      number: "",
      search: "",
      foundPrefixes: [],
      errorPrefix: "",
      errorNumber: "",
      focus: false,
      countries: [],
      showAllPrefixList: false,
    };

    this.toggleContainer = React.createRef();

    this.onClickOutsideHandler = this.onClickOutsideHandler.bind(this);
  }

  componentDidMount = () => {
    const { notScroll } = this.props;
    if (!notScroll) {
      setTimeout(function () {
        window.scrollTo(0, 0);
      }, 100);
    }
    window.addEventListener("click", this.onClickOutsideHandler);
    const { initialValue } = this.props;
    if (initialValue && initialValue !== "") {
      const phone: any = initialValue.split(" ");
      this.setState({ prefix: phone[0], number: phone[1] });
    }
    this.getCountries();
  };

  componentDidUpdate(prevProps: PhoneBoxProps) {
    const { initialValue, errorCode } = this.props;
    const { errorNumber } = this.state
    if (
      initialValue &&
      prevProps.initialValue !== initialValue &&
      initialValue !== ""
    ) {
      const phone: any = initialValue.split(" ");
      this.setState({ prefix: phone[0], number: phone[1] });
    }

    if (
      prevProps.initialValue !== initialValue &&
      (!initialValue || initialValue === "")
    ) {
      this.setState({
        showTooltip: false,
        showPrefixList: false,
        prefix: "+34",
        number: "",
        search: "",
        foundPrefixes: [],
        errorPrefix: "",
        errorNumber: "",
        focus: false,
      });
    }
    if (initialValue && initialValue.length === 13 && errorNumber === "required") {
      this.setState({
        errorNumber: "",
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.onClickOutsideHandler);
  }

  async getCountries() {
    const countries: Country[] = await getCountriesList(this.props.countries, () => {});
    const orderedCountries = countries
      .map((country) => ({ ...country, label: country.label.toLocaleUpperCase() }))
      .sort((countryA, countryB) => countryA.label.localeCompare(countryB.label));
    const defaultPrefix = countries.filter((country: Country) => country.label.includes("ESPA"));
    this.setState({ countries: orderedCountries, foundPrefixes: defaultPrefix });
  }

  onClickOutsideHandler(event: Event) {
    if (
      this.state.showPrefixList &&
      !this.toggleContainer.current.contains(event.target)
    ) {
      this.setState({ showPrefixList: false });
    } else if (
      this.state.showAllPrefixList &&
      !this.toggleContainer.current.contains(event.target)
    ) {
      this.setState({ showAllPrefixList: false });
    }
  }

  searchPrefix(search: string) {
    const searchPrefix = search.replace("+", "");

    this.setState({ prefix: "", search, showPrefixList: true }, () => {
      if (!search) {
        return this.setState({ foundPrefixes: [] });
      }
      if (!hasNumber(searchPrefix)) {
        return this.searchPrefixByCountryName(search);
      }
      if (isNumeric(searchPrefix)) {
        return this.searchPrefixByNumber(searchPrefix);
      }
    });
  }

  searchPrefixByCountryName(search: string) {
    const upperCaseSearch = search.toUpperCase();
    const { countries } = this.state;
    const foundPrefixes = countries
      .filter((country: Country) => {
        return country.label.includes(upperCaseSearch);
      })
      .sort((countryA, countryB) => {
        const aStartsWithSearch = countryA.label.startsWith(upperCaseSearch) ? 1 : 0;
        const bStartsWithSearch = countryB.label.startsWith(upperCaseSearch) ? 1 : 0;
        return bStartsWithSearch - aStartsWithSearch;
      });
    this.setState({ foundPrefixes });
  }

  searchPrefixByNumber(search: string) {
    const { countries } = this.state;
    const foundPrefixes = countries
      .filter((country: Country): country is Required<Country> => {
        return !!(country.prefix && country.prefix.includes(search));
      })
      .sort((countryA, countryB) => {
        const aStartsWithSearch = countryA.prefix.startsWith(search) ? 1 : 0;
        const bStartsWithSearch = countryB.prefix.startsWith(search) ? 1 : 0;
        return bStartsWithSearch - aStartsWithSearch;
      });
    this.setState({ foundPrefixes });
  }

  onChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value) {
      isValidNumber(e.target.value) && this.handleNumberChange(e.target.value);
      return;
    }
    if (e.target.value === "") {
      this.handleNumberChange(e.target.value);
    }
  }

  onChangeHandlerPrefix(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value) {
      const prefixNumber = e.target.value.slice(1);
      isValidNumber(prefixNumber) && this.handleBlur("prefix", e.target.value);
    }
    if (e.target.value === "+") {
      this.handleBlur("prefix", "+");
    }
  }

  validate(field: string, callback?: Function) {
    const { required } = this.props;
    const { prefix, number } = this.state;

    if (required) {
      let error = "";
      if (field === "prefix") {
        if (prefix === "" || prefix === "+") {
          error = "required";
        }
        this.setState({ errorPrefix: error }, () => {
          if (callback) callback();
        });
      } else if (field === "number") {
        if (number === "") {
          error = "required";
        }
        this.setState({ errorNumber: error }, () => {
          if (callback) callback();
        });
      }
    } else {
      if (callback) callback();
    }
  }

  handleBlur(field: string, value: string) {
    const { onChange } = this.props;
    this.setState(
      { [field]: value, focus: false },
      () => {
        this.validate(field, () => {
          const { prefix, number, errorPrefix, errorNumber } = this.state;
          let hasError = errorPrefix !== "" || errorNumber !== "";
          let phone =
            prefix.charAt(0) === "+"
              ? `${prefix} ${number || ""}`
              : `+${prefix} ${number || ""}`;
          onChange(phone, hasError);
        });
      }
    );
  }

  handleSelectPrefix(prefix: string) {
    const { onChange } = this.props;
    this.setState(
      {
        prefix,
        search: prefix,
        showPrefixList: false,
        showAllPrefixList: false,
      },
      () => {
        const { prefix, number } = this.state;
        let phone =
          prefix.charAt(0) === "+"
            ? `${prefix} ${number}`
            : `+${prefix} ${number}`;
        onChange(phone);
      }
    );
  }

  handleNumberChange(number: string) {
    const { onChange } = this.props;
    this.setState({ number }, () => {
      const { prefix, number } = this.state;
      let phone =
        prefix.charAt(0) === "+"
          ? `${prefix} ${number}`
          : `+${prefix} ${number}`;
      onChange(phone);
    });
  }

  render() {
    const {
      disabled,
      className,
      labelText,
      errorCode,
      withTooltip,
      tooltipTitle,
      tooltipText,
      required,
    } = this.props;
    const {
      showTooltip,
      showPrefixList,
      prefix,
      number,
      search,
      foundPrefixes,
      errorPrefix,
      errorNumber,
      focus,
      showAllPrefixList,
      countries,
    } = this.state;

    const error_text_prefix = errorPrefix
      ? ERROR_MESSAGES[errorPrefix]
      : errorCode && errorCode.includes("prefix")
      ? ERROR_MESSAGES[errorCode]
      : "";
    const error_text_number = errorNumber
      ? ERROR_MESSAGES[errorNumber]
      : errorCode && !errorCode.includes("prefix")
      ? ERROR_MESSAGES[errorCode]
      : "";

    return (
      <PhoneBoxContainer
        className={`${className} ${disabled ? "disabled" : ""} ${required ? "required" : ""
          }`}
      >
        <div className="input-box-topbar">
          <div className="input-box-topbar-label">
            <LabelText
              color={showPrefixList || focus ? colors["SC-001-100"] : ""}
            >
              <p>{labelText}</p>
            </LabelText>
          </div>

          {withTooltip && (
            <div
              className="input-box-topbar-icon"
              onClick={() =>
                this.setState((prevState: PhoneBoxState) => ({
                  showTooltip: !prevState.showTooltip,
                }))
              }
            >
              <img src={disabled ? images.IconInfoGrey : images.IconInfoBlue} />
              <div className="input-box-tooltip">
                <TooltipBox
                  active={showTooltip}
                  tooltipTitle={tooltipTitle}
                  tooltipText={tooltipText}
                />
              </div>
            </div>
          )}
        </div>

        <div className="input-box-main">
          <div
            ref={this.toggleContainer}
            className={`input-box-main-field-select ${error_text_prefix ? "error" : ""}  ${
              prefix !== "" ? "complete" : ""
            }`}
            onClick={() => {
              this.searchPrefixByNumber(prefix.replace("+", ""));
              this.setState(() => ({
                showPrefixList: true,
                showAllPrefixList: false,
              }));
            }}
          >
            <div className="input-box-main-field-select-inputs">
              <input
                disabled={disabled}
                type="text"
                autoComplete="nope"
                value={
                  prefix === ""
                    ? search
                    : prefix.charAt(0) === "+"
                      ? prefix
                      : `+${prefix}`
                }
                onChange={(e) => {
                  this.searchPrefix(e.target.value);
                }}
                onBlur={(e) => {
                  this.handleBlur("prefix", e.target.value);
                }}
                onFocus={(e) => this.setState({ focus: true })}
              />
              <div
                className="input-box-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState({
                    showAllPrefixList: !this.state.showAllPrefixList,
                    showPrefixList: false,
                  });
                }}
              >
                <img
                  src={
                    disabled
                      ? images.IconArrowDownGrey
                      : showAllPrefixList
                        ? images.IconArrowUpLightBlue
                        : images.IconArrowDownBlue
                  }
                />
              </div>
            </div>
            {error_text_prefix && (
              <div className="input-box-error">
                <NoteText>
                  <p>{`* ${error_text_prefix}`}</p>
                </NoteText>
              </div>
            )}
          </div>

          {showPrefixList && (
            <ul className="input-box-main-list">
              {foundPrefixes.length <= 0 ? (
                <li className="no-data">
                  <p>No se han encontrado resultados.</p>
                </li>
              ) : (
                foundPrefixes.map((prefix, i) => {
                  if (!prefix.prefix || typeof prefix.prefix !== "string") return null;
                  const prefixValue = prefix.prefix
                  return (
                    <li
                      key={i}
                      onClick={() => {
                        this.handleSelectPrefix(prefixValue);
                      }}
                    >
                      <BodyText>
                        <p onClick={() => this.handleSelectPrefix(prefixValue)}>
                          {`${prefix.label}`}
                          <span>{`+${prefix.prefix}`}</span>
                        </p>
                      </BodyText>
                    </li>
                  );
                })
              )}
            </ul>
          )}

          {showAllPrefixList && (
            <ul className="input-box-main-list">
              {countries.length <= 0 ? (
                <li className="no-data">
                  <p>No se han encontrado resultados.</p>
                </li>
              ) : (
                countries.map((prefix, i) => {
                  if (!prefix.prefix || typeof prefix.prefix !== "string") return null;
                  const prefixValue = prefix.prefix;
                  return (
                    <li
                      key={i}
                      onClick={() => {
                        this.handleSelectPrefix(prefixValue);
                      }}
                    >
                      <BodyText>
                        <p>
                          {`${prefix.label}`}
                          <span>{`+${prefix.prefix}`}</span>
                        </p>
                      </BodyText>
                    </li>
                  );
                })
              )}
            </ul>
          )}

          <div
            className={`input-box-main-field-input ${error_text_number ? "error" : ""}  ${
              number !== "" ? "complete" : ""
            }`}
          >
            <input
              disabled={disabled}
              type="text"
              autoComplete="nope"
              value={number}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { this.onChangeHandler(e) }}
              onBlur={(e) => {
                this.handleBlur("number", e.target.value);
              }}
              onFocus={(e) => this.setState({ focus: true })}
              maxLength={prefix === "+34" || prefix === "+351" ? 9 : 13}
            />
            {error_text_number && (
              <div className="input-box-error">
                <NoteText>
                  <p>{`* ${error_text_number}`}</p>
                </NoteText>
              </div>
            )}
          </div>
        </div>
      </PhoneBoxContainer>
    );
  }
}

export default withGeneral(PhoneBox);
